import i18next from "i18next";
import Cookies from "js-cookie";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Bounce, toast, ToastContainer, ToastOptions } from "react-toastify";
import { getGlobalStates } from "userful-chronos-app-common-js/dist/globalstates/globalStates";
import { registerMsgHandler } from "userful-chronos-app-common-js/dist/message/messageRegistery";
import { NOTIFY_UCLIENT_UPDATED } from "userful-chronos-app-common-js/dist/message/messageTypeDefinitions/displays/UClientsMsgDefinations";
import { NOTIFY_NOTIFICATION } from "userful-chronos-app-common-js/dist/message/messageTypeDefinitions/notifications/notificationMsgs";
import { isIDNotSet } from "userful-chronos-app-common-js/dist/models/common";
import { prettyPrintStationLabel } from "userful-chronos-app-common-js/dist/models/display";
import { getMultiWindowGroupName } from "userful-chronos-app-common-js/dist/models/mapping/MappingGroups";
import {
    AetherNotification,
    interpretErrorTypePreffix,
    VEMNotificationData,
} from "userful-chronos-app-common-js/dist/models/notifications/notifications";
import { getUClientName, isUClientOffline, UClientComp } from "userful-chronos-app-common-js/dist/models/uclient";
import { UserPermission } from "userful-chronos-app-common-js/dist/models/user-permission";
import { useChoppedAssetSelector } from "userful-chronos-common-store/dist/sourceassets";
import NotificationToast, { COOKIE_KEY_PREFIX } from "./NotificationToast";

const toastOptions: ToastOptions = {
    position: "top-right",
    autoClose: false,
    className: "notificationTostify",
    containerId: "customContainer",
};

export const usageAlertOptions: ToastOptions = {
    ...toastOptions,
};

const shouldShow = (key: string): boolean => {
    return !Cookies.get(`${COOKIE_KEY_PREFIX}${key}`);
};

const isAdmin = (permission: UserPermission): boolean =>
    permission.isAdminUser ||
    permission.isDecisionsAdmin ||
    permission.isEngageAdmin ||
    permission.isRubyAdmin ||
    permission.isSystemConfigAdmin ||
    permission.isUserManagementAdmin ||
    permission.isTabletCtrlAdmin ||
    permission.isSapphireAdmin;

const NotificationsContainer = () => {
    const { t } = useTranslation();
    const userPermission = getGlobalStates().userPermission;

    const choppingRequests = useChoppedAssetSelector((state) => state.choppedAssetStore.currentChoppingRequests);
    const choppingRef = useRef(choppingRequests);
    useEffect(() => {
        choppingRef.current = [...choppingRequests];
    }, [choppingRequests]);

    const hasPermission = (error: AetherNotification): boolean => {
        if (error.level === "SYSTEM") {
            return isAdmin(userPermission);
        }
        if (isIDNotSet(error.app)) {
            return true;
        }
        return error.app.value === getGlobalStates().appID.value;
    };

    useEffect(() => {
        registerMsgHandler(NOTIFY_NOTIFICATION, (payload) => {
            if (getGlobalStates().eventBus.rebooting || !getGlobalStates().showErrors) {
                // don't show errors on user-triggered reboot
                return;
            }
            const errorNotification = JSON.parse(payload) as AetherNotification;
            const errorData = errorNotification.data as VEMNotificationData;
            if (errorData["@type"] !== "vem") {
                // aether error not implemented
                return;
            }

            const errorType = `${interpretErrorTypePreffix(errorData)}${errorData.errorType}`;
            if (errorNotification.severity === "FATAL") {
                const translationKey = `Error.notificationErrors.${errorType}`;
                if (!shouldShow(translationKey) || !hasPermission(errorNotification)) {
                    return;
                }
                if (i18next.exists(translationKey)) {
                    const toastId = `${errorType}-${getMultiWindowGroupName(errorNotification.name)}-${errorData.media || errorData.errorData}`;
                    if (toast.isActive(toastId)) {
                        return;
                    }
                    toast.error(
                        <NotificationToast
                            cookieKey={translationKey}
                            usageLevel={null}
                            content={t(translationKey, {
                                name: getMultiWindowGroupName(errorNotification.name),
                                errorData: errorData?.errorData,
                                media: errorData.media?.replace("file:///var/source-content/", ""),
                            })}
                            hideToast={() => toast.dismiss(toastId)}
                        />,
                        { ...toastOptions, toastId, autoClose: false, position: "top-center" },
                    );
                } else {
                    console.error(errorNotification.msg);
                }
            } else if (errorNotification.severity === "WARN") {
                const translationKey = `Error.notificationWarnings.${errorType}`;
                if (!shouldShow(translationKey) || !hasPermission(errorNotification)) {
                    return;
                }
                if (i18next.exists(translationKey)) {
                    toast.warn(
                        <NotificationToast
                            cookieKey={translationKey}
                            usageLevel={null}
                            content={t(translationKey, {
                                name: getMultiWindowGroupName(errorNotification.name),
                                media: errorData.media?.replace("file:///var/source-content/", ""),
                            })}
                        />,
                        { ...toastOptions, toastId: errorType, autoClose: false, position: "top-center" },
                    );
                } else {
                    console.warn(errorNotification.msg);
                }
            }
        });
        if (isAdmin(userPermission)) {
            registerMsgHandler(NOTIFY_UCLIENT_UPDATED, (payload) => {
                if (getGlobalStates().eventBus.rebooting) {
                    // don't show errors on user-triggered reboot
                    return;
                }
                const data = JSON.parse(payload) as UClientComp;
                if (isUClientOffline(data) && data.lockStatus === "LOCKED") {
                    const key = "UCLIENT_OFFLINE";
                    if (!shouldShow(key)) {
                        return;
                    }
                    const uClientName = getUClientName(data);
                    const id = data.name || `#${prettyPrintStationLabel(data.stationLabel)}`;

                    if (!toast.isActive(`${data.id.value}-uClient`))
                        toast.error(
                            <NotificationToast
                                cookieKey={key}
                                usageLevel={null}
                                content={`${t("Shared.uClient")} ${id} ${t("Shared.uClientOffline")}`}
                                hideToast={() => toast.dismiss(`${data.id.value}-uClient`)}
                            />,
                            {
                                ...toastOptions,
                                toastId: `${data.id.value}-uClient`,
                                autoClose: 5,
                                position: "top-right",
                            },
                        );
                } else toast.dismiss(`${data.id.value}-uClient`);
            });
        }
    }, []);

    return <ToastContainer transition={Bounce} containerId="customContainer" className="customContainer" />;
};

export default NotificationsContainer;
